import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import axios from "axios";
import DateFilter from "@/components/DateFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);
  return amount < 0 ? `(${formatted})` : formatted;
}

export default function CashFlow({ companyContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          ...filters,
          company_id: companyContext?.company?.company_id || "default",
          ...(companyContext?.branch?.branch_id
            ? { branch_id: companyContext.branch.branch_id }
            : {}),
        };
        const response = await axios.get(`${API}/reports/cash-flow`, { params });
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, companyContext?.company?.company_id, companyContext?.branch?.branch_id]);

  if (error) {
    return (
      <div className="text-center py-8 text-red-600" data-testid="cf-error">
        {error}
      </div>
    );
  }

  const netCashFlow = data?.net_cash_flow || 0;
  const isPositive = netCashFlow >= 0;

  return (
    <div data-testid="cash-flow">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="cf-title">
          Cash Flow Statement
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Summary of cash receipts and payments
        </p>
      </div>

      <DateFilter onFilterChange={setFilters} />

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="cf-loading">
          <div className="spinner"></div>
        </div>
      ) : (<>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowDownCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Receipts
                </p>
                <p className="text-xl font-bold text-green-600 mt-1 font-mono tabular-nums">
                  ₹ {formatCurrency(data?.total_receipts)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowUpCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Payments
                </p>
                <p className="text-xl font-bold text-red-600 mt-1 font-mono tabular-nums">
                  ₹ {formatCurrency(data?.total_payments)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Net Cash Flow
            </p>
            <p
              className={`text-xl font-bold mt-1 font-mono tabular-nums ${
                isPositive ? "text-green-600" : "text-red-600"
              }`}
            >
              ₹ {formatCurrency(netCashFlow)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Receipts */}
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2 bg-green-50">
            <CardTitle className="text-sm font-semibold text-green-800">
              Receipts ({data?.receipts?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-auto">
              <Table data-testid="receipts-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Party</TableHead>
                    <TableHead className="text-right font-semibold w-32">Amount (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.receipts?.map((item, idx) => (
                    <TableRow key={`receipt-${item.date}-${item.party}-${idx}`} data-testid={`receipt-row-${idx}`}>
                      <TableCell className="text-sm">
                        {item.date?.split("T")[0] || "-"}
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">
                        {item.party || "-"}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-green-600">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.receipts || data.receipts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                        No receipt data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Payments */}
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2 bg-red-50">
            <CardTitle className="text-sm font-semibold text-red-800">
              Payments ({data?.payments?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-auto">
              <Table data-testid="payments-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Party</TableHead>
                    <TableHead className="text-right font-semibold w-32">Amount (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.payments?.map((item, idx) => (
                    <TableRow key={`payment-${item.date}-${item.party}-${idx}`} data-testid={`payment-row-${idx}`}>
                      <TableCell className="text-sm">
                        {item.date?.split("T")[0] || "-"}
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">
                        {item.party || "-"}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-red-600">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.payments || data.payments.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                        No payment data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
      )}
    </div>
  );
}
